import React, { FC, useEffect, useState } from 'react';
import { Item } from 'src/interfaces/api/ActivityWall';
import Document from 'src/interfaces/api/Document';
import TimelineElement from './TimelineElement';
import useDocument from 'src/hooks/api/useDocument';
import useCache from 'src/hooks/useCache';
import useActivityWall from 'src/hooks/api/useActivityWall';
import ConfirmationDialog from 'src/components/ConfirmationDialog';

interface Props {
  item: Item;
  wallId: string;
}

const TimelineElementContainer: FC<Props> = ({ wallId, item }) => {
  const [attachments, setAttachments] = useState<Document[]>([]);
  const [deleteItemDialogOpen, setDeleteItemDialogOpen] = useState(false);
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const { getDocuments } = useDocument();
  const [items, setItems] = useCache<Item[]>();

  const { removeItemAttachment, addItemAttachment, removePost, editPost } = useActivityWall();

  useEffect(() => {
    if (item.attachmentsIds && item.attachmentsIds.length > 0) {
      setLoading(true);
      setError(undefined);

      getDocuments(item.attachmentsIds)
        .then((resp) => {
          setAttachments(resp.items);
          setLoading(false);
        })
        .catch((e) => {
          setError(e.message);
          setLoading(false);
        });
    }
  }, [item]);

  const handleAddAttachment = async (attachment: File) => {
    setError(undefined);
    setLoading(true);
    try {
      const newItem = await addItemAttachment(wallId, item.id, attachment);

      const cachedItemIndex = items?.findIndex((cachedItem) => cachedItem.id === item.id) ?? -1;
      const cachedItems = [...(items || [])];

      cachedItems[cachedItemIndex] = newItem;
      setItems(cachedItems);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const handleEditItem = async (title: string, comment: string) => {
    const itemId = item.id;
    const newItem = await editPost(wallId, itemId, title, comment);

    const cachedItemIndex = items?.findIndex((cachedItem) => cachedItem.id === item.id) ?? -1;
    const cachedItems = [...(items || [])];
    cachedItems[cachedItemIndex] = newItem;

    setItems(cachedItems);
  };

  const handleRemoveItem = async () => {
    try {
      const itemId = item.id;
      await removePost(wallId, itemId);

      const newItems = (items || []).filter((item) => item.id !== itemId);

      setItems(newItems);
    } catch (e) {
      setError(e.message);
    }

    setDeleteItemDialogOpen(false);
  };

  const handleRemoveAttachment = async (documentId: string) => {
    try {
      await removeItemAttachment(wallId, item.id, documentId);

      const cachedItemIndex = items?.findIndex((cachedItem) => cachedItem.id === item.id) ?? -1;
      const cachedItems = [...(items || [])];
      const cachedItemAttachementsIds = cachedItems[cachedItemIndex].attachmentsIds ?? [];
      cachedItems[cachedItemIndex].attachmentsIds = cachedItemAttachementsIds.filter(
        (attachmentId) => attachmentId !== documentId
      );

      setItems(cachedItems);
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <>
      <TimelineElement
        item={item}
        employeeId={item.initiator.type === 'employee' ? item.initiator.id : undefined}
        attachments={attachments}
        error={error}
        onEditItem={handleEditItem}
        onDeleteItem={() => setDeleteItemDialogOpen(true)}
        onAddAttachment={handleAddAttachment}
        onRemoveAttachment={handleRemoveAttachment}
        attachmentsLoading={loading}
      />

      <ConfirmationDialog
        title='Attention'
        description='Vous êtes sur le point de supprimer un commentaire. Êtes-vous sûr ?'
        confirmationText='Supprimer'
        onClose={() => setDeleteItemDialogOpen(false)}
        open={deleteItemDialogOpen}
        onValidate={handleRemoveItem}
      />
    </>
  );
};

export default TimelineElementContainer;
